
import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  effect,
  untracked,
  DestroyRef,
} from '@angular/core'
import {
  Entity,
  EntityDetails,
  EntityRelationship,
  EntityRelationshipMap,
  EntityRelationshipType,
  EntityTypeCode,
  EntityTypeMap,
  EntityTypeMetadata,
  RelationshipType,
} from './entity-metadata'
import { EntityEditorTitleComponent } from './entity-editor-title.component'
import { map, Observable, of, zip } from 'rxjs'
import { EntityManagementService } from './entity-management.service'
import { EntityDetailsEditorComponent } from './entity-details-editor.component'
import { AutoHideSpinnerComponent } from '../public/spinner/auto-hide-spinner.component'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { EntityRelationshipsEditorComponent } from './entity-relationships-editor.component'
import { MinMax } from '../shared/models/general'

type TabKey = 'details' | EntityTypeCode

type Tab = Readonly<{ key: TabKey, name: string, relationshipType?: RelationshipType, disabled?: boolean }>

const detailsTab: Tab = { key: 'details', name: 'Details' }

const disabledDetailsTab = { ...detailsTab, disabled: true }

type GetDataResult = [readonly EntityDetails[], EntityRelationshipMap, EntityRelationshipMap]

export type RelationshipSelection = Readonly<{
  relationshipType: RelationshipType,
  relatedEntityType: EntityTypeCode
}>

@Component({
  selector: 'app-entity-editor',
  imports: [
    EntityEditorTitleComponent,
    AutoHideSpinnerComponent,
    EntityDetailsEditorComponent,
    EntityRelationshipsEditorComponent,
  ],
  template: `
    <h3 app-entity-title
        [entity]="entity()"
        [dateRange]="dateRange()"
        [entityTypeMetadata]="entityTypeMetadata()"
    >
    </h3>
    <ul class="nav nav-tabs">
      @for (tab of tabs(); track tab.key) {
        <li class="nav-item">
          <button
              class="nav-link"
              [disabled]="fetching() || tab.disabled"
              [class.active]="activeTab() === tab && !tab.disabled"
              (click)="onTabSelected($event, tab)"
          >
            {{ tab.name }}
          </button>
        </li>
      }
    </ul>
    <div class="tab-content">
      @if (waiting() || activeTab().disabled) {
        <app-autohide-spinner/>
      } @else if (activeTab().key === 'details') {
        <app-entity-details-editor
            [entity]="entity()!"
            [url]="url()"
            [entityDetails]="entityDetails()"
            [entityTypeMetadata]="entityTypeMetadata()!"
            (saveComplete)="onEntityDetailsSaveComplete()"
            (error)="error.emit($event)"
        />
      } @else if (activeTab().relationshipType) {
        <app-entity-relationships-editor
            [relationshipType]="activeTab().relationshipType!"
            [entity]="entity()!"
            [url]="url()"
            [entityTypeMetadata]="getTabMetadata()!"
            [entityRelationships]="getTabRelationships()"
            (saveComplete)="onRelationshipsSaveComplete($event)"
            (error)="error.emit($event)"
        />
      }
    </div>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      
      .nav-tabs {
        .nav-item {
          .nav-link {
            &:disabled {
              opacity: 0.5;
            }
          }
        }
      }
      .tab-content {
        flex-basis: 0;
        min-height: 0;
        flex-grow: 1;
        padding: 1rem;
        border-left: 1px solid #dee2e6;
        border-bottom: 1px solid #dee2e6;
        border-right: 1px solid #dee2e6;
        border-bottom-left-radius: 8px;
        border-bottom-right-radius: 8px;
        overflow: auto;
      }
    }
  `,
})
export class EntityEditorComponent {
  private readonly entityManagementService = inject(EntityManagementService)
  private readonly destroyRef = inject(DestroyRef)

  readonly entity = input.required<Entity | undefined>()
  readonly url = input.required<string>()
  readonly tabSelection = input.required<RelationshipSelection | undefined>()
  readonly entityTypeMap = input.required<EntityTypeMap | undefined>()
  readonly entityRelationshipTypes = input.required<readonly EntityRelationshipType[]>()

  protected readonly entityDetails = signal<readonly EntityDetails[]>([])
  protected readonly parentRelationships = signal<Readonly<EntityRelationshipMap>>({})
  protected readonly childRelationships = signal<Readonly<EntityRelationshipMap>>({})
  protected readonly fetching = signal(false)

  readonly tabSelectionChange = output<RelationshipSelection | undefined>()
  readonly error = output<Error>()
  readonly saveComplete = output()

  protected readonly entityTypeMetadata = computed(() => {
    const entity = this.entity()
    const entityTypeMap = this.entityTypeMap()
    if (entity && entityTypeMap) {
      return entityTypeMap[entity.entity_type_code]
    } else {
      return undefined
    }
  })

  protected readonly activeTab = computed((): Tab => {
    const tabSelection = this.tabSelection()
    if (tabSelection) {
      const tab =  this.tabs().find(_ =>
        _.relationshipType === tabSelection.relationshipType && _.key === tabSelection.relatedEntityType,
      )
      if (tab) {
        return tab
      }
    }
    return this.entityTypeMetadata()?.editable ? detailsTab : disabledDetailsTab
  })

  protected readonly waiting = computed((): boolean => this.fetching() || !this.activeTab())

  protected readonly dateRange = computed((): MinMax | undefined => {
    const details = this.entityDetails()[0]
    if (details && details.termination_date < '9') {
      return { min: details.effective_date, max: details.termination_date }
    } else {
      return undefined
    }
  })

  protected readonly tabs = computed((): readonly Tab[] => {
    const entityTypeMap = this.entityTypeMap()

    const defaultTabs = this.entityTypeMetadata()?.editable ? [detailsTab] : [disabledDetailsTab]

    if (entityTypeMap) {
      const getRelationshipTabs = (relationshipType: RelationshipType): readonly Tab[] =>
        this.relationshipEntityTypes(relationshipType).map(entityTypeCode => ({
          key: entityTypeCode,
          name: `${entityTypeMap[entityTypeCode].entity_type_code}${relationshipType === 'parent' ? '' : 's'}`,
          relationshipType,
        }))

      return [...defaultTabs, ...getRelationshipTabs('parent'), ...getRelationshipTabs('child')]
    } else {
      return defaultTabs
    }
  })


  constructor() {
    toObservable(this.entity).subscribe(next => {
      if (next) {
        this.refresh(next)
      }
    })

    effect(() => {
      const activeTab = this.activeTab()
      const tabs = this.tabs()
      untracked(() => {
        if (activeTab.disabled) {
          const toSelect = tabs.find(_ => !_.disabled && _.relationshipType)
          if (toSelect) {
            this.tabSelectionChange.emit({
              relationshipType: toSelect.relationshipType!,
              relatedEntityType: toSelect.key as EntityTypeCode,
            })
          }
        }
      })
    })
  }

  private refresh(entity: Entity) {
    this.entityDetails.set([])
    this.parentRelationships.set({})
    this.childRelationships.set({})
    this.fetching.set(true)
    this.getData(entity).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ([details, parentRelationships, childRelationships]: GetDataResult) => {
        this.entityDetails.set(details)
        this.parentRelationships.set(parentRelationships)
        this.childRelationships.set(childRelationships)
        this.fetching.set(false)
      },
      error: error => {
        this.fetching.set(false)
        this.error.emit(error as Error)
      },
    })
  }

  private getData(entity: Entity): Observable<GetDataResult> {
    return zip([
      this.getEntityDetails(entity),
      this.getAllRelationships(entity, 'parent'),
      this.getAllRelationships(entity, 'child'),
    ])
  }

  private getEntityDetails({ entity_uid, entity_type_code }: Entity): Observable<readonly EntityDetails[]> {
    return this.entityManagementService.getEntityDetails(entity_type_code, entity_uid).pipe(
      takeUntilDestroyed(this.destroyRef),
    )
  }

  private getAllRelationships(entity: Entity, relationshipType: RelationshipType): Observable<EntityRelationshipMap> {
    const getTypeCodes = () => {
      const entityRelationshipTypes = this.entityRelationshipTypes()
      if (relationshipType === 'parent') {
        return entityRelationshipTypes.filter(
          _ => _.child_entity_type_code === entity.entity_type_code,
        ).map(_ => _.parent_entity_type_code)
      } else {
        return entityRelationshipTypes.filter(
          _ => _.parent_entity_type_code === entity.entity_type_code,
        ).map(_ => _.child_entity_type_code)
      }
    }

    const typeCodes = getTypeCodes()
    if (typeCodes.length == 0) {
      return of({})
    } else {
      return zip(
        typeCodes.map(typeCode =>
          this.getRelationships(entity, typeCode, relationshipType).pipe(
            map((results): [EntityTypeCode, EntityRelationship[]] =>
              [typeCode, results]),
          ),
        ),
      ).pipe(
        map(arrayOfResults => {
          const result: EntityRelationshipMap = {}
          arrayOfResults.forEach(([entityTypeCode, relationships]) => {
            result[entityTypeCode] = relationships
          })
          return result
        }),
      )
    }
  }

  private getRelationships(
    entity: Entity,
    entityTypeCode: EntityTypeCode,
    relationshipType: RelationshipType,
  ): Observable<EntityRelationship[]> {
    return this.entityManagementService.getRelationships(entity, entityTypeCode, relationshipType)
      .pipe(takeUntilDestroyed(this.destroyRef))
  }

  protected onEntityDetailsSaveComplete() {
    this.saveComplete.emit() // refresh entities
    this.getEntityDetails(this.entity()!).subscribe({
      next: details => {
        this.entityDetails.set(details)
      },
      error: e => {
        this.error.emit(e as Error)
      },
    })
  }

  protected getTabMetadata(): EntityTypeMetadata | undefined {
    const selection = this.tabSelection()
    const entityTypeMap = this.entityTypeMap()
    if (selection && entityTypeMap) {
      return entityTypeMap[selection.relatedEntityType]
    } else {
      return undefined
    }
  }

  protected getTabRelationships(): EntityRelationship[] {
    const activeTab = this.activeTab()
    if (activeTab) {
      const { key, relationshipType } = activeTab
      const lookup = relationshipType === 'parent'
        ? this.parentRelationships()
        : this.childRelationships()
      return lookup[key as EntityTypeCode]!
    } else {
      return []
    }
  }

  private relationshipEntityTypes (relationshipType: RelationshipType): readonly EntityTypeCode[] {
    return Object.keys (
      relationshipType === 'parent' ? this.parentRelationships() : this.childRelationships(),
    ) as readonly EntityTypeCode[]
  }

  protected onTabSelected (event: Event, { relationshipType, key }: Tab) {
    event.preventDefault()
    this.tabSelectionChange.emit(relationshipType ? { relationshipType, relatedEntityType: key as EntityTypeCode } : undefined)
  }

  protected onRelationshipsSaveComplete(entityTypeCode: EntityTypeCode) {
    this.fetching.set(true)
    this.getRelationships(this.entity()!, entityTypeCode, 'parent').subscribe({
      next: relationships => {
        this.fetching.set(false)
        this.parentRelationships.set({
          ...this.parentRelationships(),
          [entityTypeCode]: relationships,
        })
      },
      error: error => {
        this.fetching.set(false)
        this.error.emit(error as Error)
      },
    })
  }
}
